import { Component, computed, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatIconModule } from '@angular/material/icon';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { ResponsiveBaseComponent } from 'shared-lib';
import { TariffService, TariffVersionService } from '../../api/gen';
import {
  GracePeriodDto,
  RateBandDto,
  RateCapDto,
  RateTierDto,
  RuleScopeField,
  TARIFF_STATUSES,
  TariffDto,
  TariffRuleDto,
  TariffStatus,
  TariffVersionDto,
} from './models/tariff.models';
import { TariffFormDialogComponent } from './tariff-form-dialog/tariff-form-dialog.component';
import { TariffRuleDialogComponent } from './tariff-rule-dialog/tariff-rule-dialog.component';
import { TariffVersionDialogComponent } from './tariff-version-dialog/tariff-version-dialog.component';
import { ConfirmDialogComponent, ConfirmDialogData } from './confirm-dialog/confirm-dialog.component';

const SEARCH_DEBOUNCE_MS = 300;

@Component({
  selector: 'app-tariff-management',
  standalone: true,
  imports: [CommonModule, FormsModule, MatDialogModule, MatIconModule, TranslatePipe],
  templateUrl: './tariff-management.component.html',
  styleUrl: './tariff-management.component.scss',
})
export class TariffManagementComponent extends ResponsiveBaseComponent implements OnInit, OnDestroy {
  private readonly api = inject(TariffService);
  private readonly versionApi = inject(TariffVersionService);
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);
  private readonly t = inject(TranslateService);

  readonly tariffs = signal<TariffDto[]>([]);
  readonly loading = signal(false);
  readonly errorMessage = signal<string | null>(null);

  readonly statusFilter = signal<TariffStatus | null>(null);
  readonly searchName = signal('');
  readonly pageIndex = signal(1); // 1-based, matches backend
  readonly pageSize = signal(20);
  readonly totalCount = signal(0);

  readonly expandedId = signal<string | null>(null);
  readonly busyId = signal<string | null>(null);

  readonly statuses = TARIFF_STATUSES;
  protected readonly Math = Math;

  readonly totalPages = computed(() => Math.max(1, Math.ceil(this.totalCount() / this.pageSize())));
  private readonly dialogWidth = computed(() => (this.isHandset() ? '95vw' : '540px'));
  /** The rule and version wizards need more room for their step content than the simple dialogs. */
  private readonly wizardWidth = computed(() => (this.isHandset() ? '95vw' : '860px'));

  private searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;

  ngOnInit(): void {
    this.load();
  }

  ngOnDestroy(): void {
    if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);
  }

  load(): void {
    this.loading.set(true);
    this.errorMessage.set(null);
    const name = this.searchName().trim() || undefined;
    this.api.tariffsGet(this.statusFilter() ?? undefined, name, this.pageIndex(), this.pageSize()).subscribe({
      next: (result) => {
        this.tariffs.set(result?.items ?? []);
        this.totalCount.set(result?.totalCount ?? 0);
        this.loading.set(false);
      },
      error: (err) => {
        console.error('Error loading tariffs:', err);
        this.errorMessage.set(this.t.instant('tariff.loadError'));
        this.loading.set(false);
      },
    });
  }

  setFilter(status: TariffStatus | null): void {
    if (this.statusFilter() === status) return;
    this.statusFilter.set(status);
    this.pageIndex.set(1);
    this.load();
  }

  /** Debounced live search — reloads page 1 shortly after the user stops typing. */
  onSearchChange(value: string): void {
    this.searchName.set(value);
    this.pageIndex.set(1);
    if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);
    this.searchDebounceTimer = setTimeout(() => this.load(), SEARCH_DEBOUNCE_MS);
  }

  clearSearch(): void {
    if (!this.searchName()) return;
    if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);
    this.searchName.set('');
    this.pageIndex.set(1);
    this.load();
  }

  goToPage(index: number): void {
    if (index < 1 || index > this.totalPages()) return;
    this.pageIndex.set(index);
    this.load();
  }

  toggleExpand(id: string | undefined): void {
    if (!id) return;
    this.expandedId.update((cur) => (cur === id ? null : id));
  }

  // ---- CRUD ----

  async openCreate(): Promise<void> {
    const request = await firstValueFrom(
      this.dialog
        .open(TariffFormDialogComponent, { width: this.dialogWidth(), maxWidth: this.dialogWidth(), data: {} })
        .afterClosed(),
    );
    if (!request) return;
    this.api.tariffsPost(request).subscribe({
      next: () => {
        this.notify(this.t.instant('tariff.msg.created'));
        this.load();
      },
      error: (err) => this.notifyError(err, this.t.instant('tariff.msg.createFailed')),
    });
  }

  async openEdit(tariff: TariffDto): Promise<void> {
    const request = await firstValueFrom(
      this.dialog
        .open(TariffFormDialogComponent, { width: this.dialogWidth(), maxWidth: this.dialogWidth(), data: { tariff } })
        .afterClosed(),
    );
    if (!request) return;
    this.busyId.set(tariff.id ?? null);
    this.api.tariffsIdPut(tariff.id!, request).subscribe({
      next: () => {
        this.busyId.set(null);
        this.notify(this.t.instant('tariff.msg.updated'));
        this.load();
      },
      error: (err) => {
        this.busyId.set(null);
        this.notifyError(err, this.t.instant('tariff.msg.updateFailed'));
      },
    });
  }

  activate(tariff: TariffDto): void {
    this.busyId.set(tariff.id ?? null);
    this.api.tariffsIdActivatePost(tariff.id!).subscribe({
      next: () => {
        this.busyId.set(null);
        this.notify(this.t.instant('tariff.msg.activated'));
        this.load();
      },
      error: (err) => {
        this.busyId.set(null);
        this.notifyError(err, this.t.instant('tariff.msg.activateFailed'));
      },
    });
  }

  async archive(tariff: TariffDto): Promise<void> {
    const ok = await this.confirm({
      title: this.t.instant('tariff.confirm.archiveTitle'),
      message: this.t.instant('tariff.confirm.archiveBody', { name: tariff.name }),
      confirmLabel: this.t.instant('tariff.confirm.archiveBtn'),
    });
    if (!ok) return;
    this.busyId.set(tariff.id ?? null);
    this.api.tariffsIdArchivePost(tariff.id!).subscribe({
      next: () => {
        this.busyId.set(null);
        this.notify(this.t.instant('tariff.msg.archived'));
        this.load();
      },
      error: (err) => {
        this.busyId.set(null);
        this.notifyError(err, this.t.instant('tariff.msg.archiveFailed'));
      },
    });
  }

  async remove(tariff: TariffDto): Promise<void> {
    const ok = await this.confirm({
      title: this.t.instant('tariff.confirm.deleteTitle'),
      message: this.t.instant('tariff.confirm.deleteBody', { name: tariff.name }),
      confirmLabel: this.t.instant('tariff.confirm.deleteBtn'),
      danger: true,
    });
    if (!ok) return;
    this.busyId.set(tariff.id ?? null);
    this.api.tariffsIdDelete(tariff.id!).subscribe({
      next: () => {
        this.busyId.set(null);
        this.notify(this.t.instant('tariff.msg.deleted'));
        this.load();
      },
      error: (err) => {
        this.busyId.set(null);
        this.notifyError(err, this.t.instant('tariff.msg.deleteFailed'));
      },
    });
  }

  // ---- Rules ----

  /** The rule wizard owns its own submission (rule + any staged conditions) — it only reports
   *  back whether it succeeded, so the parent just reloads on success. */
  async addRule(tariff: TariffDto): Promise<void> {
    const created = await firstValueFrom(
      this.dialog
        .open(TariffRuleDialogComponent, { width: this.wizardWidth(), maxWidth: this.wizardWidth(), data: { tariff } })
        .afterClosed(),
    );
    if (!created) return;
    this.expandedId.set(tariff.id ?? null);
    this.notify(this.t.instant('tariff.msg.ruleAdded'));
    this.load();
  }

  async editRule(tariff: TariffDto, rule: TariffRuleDto): Promise<void> {
    const saved = await firstValueFrom(
      this.dialog
        .open(TariffRuleDialogComponent, {
          width: this.wizardWidth(),
          maxWidth: this.wizardWidth(),
          data: { tariff, rule },
        })
        .afterClosed(),
    );
    if (!saved) return;
    this.expandedId.set(tariff.id ?? null);
    this.notify(this.t.instant('tariff.msg.ruleUpdated'));
    this.load();
  }

  async removeRule(tariff: TariffDto, rule: TariffRuleDto): Promise<void> {
    const ok = await this.confirm({
      title: this.t.instant('tariff.confirm.deleteRuleTitle'),
      message: this.t.instant('tariff.confirm.deleteRuleBody'),
      confirmLabel: this.t.instant('tariff.confirm.deleteBtn'),
      danger: true,
    });
    if (!ok) return;
    this.api.tariffsRulesRuleIdDelete(rule.id!).subscribe({
      next: () => {
        this.expandedId.set(tariff.id ?? null);
        this.notify(this.t.instant('tariff.msg.ruleDeleted'));
        this.load();
      },
      error: (err) => this.notifyError(err, this.t.instant('tariff.msg.ruleDeleteFailed')),
    });
  }

  // ---- Versions ----

  /** The version wizard owns its own submission (version + any staged grace periods). */
  async addVersion(tariff: TariffDto): Promise<void> {
    const created = await firstValueFrom(
      this.dialog
        .open(TariffVersionDialogComponent, { width: this.wizardWidth(), maxWidth: this.wizardWidth(), data: { tariff } })
        .afterClosed(),
    );
    if (!created) return;
    this.expandedId.set(tariff.id ?? null);
    this.notify(this.t.instant('tariff.version.msg.created'));
    this.load();
  }

  activateVersion(tariff: TariffDto, version: TariffVersionDto): void {
    this.busyId.set(version.id ?? null);
    this.versionApi.tariffsTariffIdVersionsIdActivatePost(tariff.id!, version.id!).subscribe({
      next: () => {
        this.busyId.set(null);
        this.notify(this.t.instant('tariff.version.msg.activated'));
        this.load();
      },
      error: (err) => {
        this.busyId.set(null);
        this.notifyError(err, this.t.instant('tariff.version.msg.activateFailed'));
      },
    });
  }

  async supersedeVersion(tariff: TariffDto, version: TariffVersionDto): Promise<void> {
    const ok = await this.confirm({
      title: this.t.instant('tariff.version.confirm.supersedeTitle'),
      message: this.t.instant('tariff.version.confirm.supersedeBody'),
      confirmLabel: this.t.instant('tariff.version.confirm.supersedeBtn'),
      danger: true,
    });
    if (!ok) return;
    this.busyId.set(version.id ?? null);
    this.versionApi.tariffsTariffIdVersionsIdSupersedePost(tariff.id!, version.id!).subscribe({
      next: () => {
        this.busyId.set(null);
        this.notify(this.t.instant('tariff.version.msg.superseded'));
        this.load();
      },
      error: (err) => {
        this.busyId.set(null);
        this.notifyError(err, this.t.instant('tariff.version.msg.supersedeFailed'));
      },
    });
  }

  async deleteGracePeriod(tariff: TariffDto, version: TariffVersionDto, gracePeriod: GracePeriodDto): Promise<void> {
    const ok = await this.confirm({
      title: this.t.instant('tariff.version.confirm.deleteGraceTitle'),
      message: this.t.instant('tariff.version.confirm.deleteGraceBody'),
      confirmLabel: this.t.instant('tariff.confirm.deleteBtn'),
      danger: true,
    });
    if (!ok) return;
    this.versionApi.tariffsTariffIdVersionsIdGracePeriodsGracePeriodIdDelete(tariff.id!, version.id!, gracePeriod.id!).subscribe({
      next: () => {
        this.expandedId.set(tariff.id ?? null);
        this.notify(this.t.instant('tariff.version.msg.graceDeleted'));
        this.load();
      },
      error: (err) => this.notifyError(err, this.t.instant('tariff.version.msg.graceDeleteFailed')),
    });
  }

  // D-34: caps and tiers are pricing. Removing one from an Active or Superseded version would
  // silently re-price every historical stay that re-prices against that frozen version, so the
  // server refuses it — the confirmation says so rather than letting the operator find out via a 400.
  async deleteRateTier(tariff: TariffDto, version: TariffVersionDto, tier: RateTierDto): Promise<void> {
    const ok = await this.confirm({
      title: this.t.instant('tariff.version.confirm.deleteTierTitle'),
      message: this.t.instant('tariff.version.confirm.deleteTierBody'),
      confirmLabel: this.t.instant('tariff.confirm.deleteBtn'),
      danger: true,
    });
    if (!ok) return;
    this.versionApi.tariffsTariffIdVersionsIdRateTiersRateTierIdDelete(tariff.id!, version.id!, tier.id!).subscribe({
      next: () => {
        this.expandedId.set(tariff.id ?? null);
        this.notify(this.t.instant('tariff.version.msg.tierDeleted'));
        this.load();
      },
      error: (err) => this.notifyError(err, this.t.instant('tariff.version.msg.tierDeleteFailed')),
    });
  }

  async deleteRateBand(tariff: TariffDto, version: TariffVersionDto, band: RateBandDto): Promise<void> {
    const ok = await this.confirm({
      title: this.t.instant('tariff.version.confirm.deleteBandTitle'),
      message: this.t.instant('tariff.version.confirm.deleteBandBody'),
      confirmLabel: this.t.instant('tariff.confirm.deleteBtn'),
      danger: true,
    });
    if (!ok) return;
    this.versionApi.tariffsTariffIdVersionsIdRateBandsRateBandIdDelete(tariff.id!, version.id!, band.id!).subscribe({
      next: () => {
        this.expandedId.set(tariff.id ?? null);
        this.notify(this.t.instant('tariff.version.msg.bandDeleted'));
        this.load();
      },
      error: (err) => this.notifyError(err, this.t.instant('tariff.version.msg.bandDeleteFailed')),
    });
  }

  entriesLabel(entries: number | null | undefined): string {
    const n = Number(entries ?? 1);
    if (n === 0) return this.t.instant('tariff.version.entries.unlimited');
    if (n === 1) return this.t.instant('tariff.version.entries.single');
    return this.t.instant('tariff.version.entries.count', { count: n });
  }

  /** "HH:mm:ss" from the API → "HH:mm" for display. */
  bandTime(time: string | null | undefined): string {
    return (time ?? '').slice(0, 5);
  }

  async deleteRateCap(tariff: TariffDto, version: TariffVersionDto, cap: RateCapDto): Promise<void> {
    const ok = await this.confirm({
      title: this.t.instant('tariff.version.confirm.deleteCapTitle'),
      message: this.t.instant('tariff.version.confirm.deleteCapBody'),
      confirmLabel: this.t.instant('tariff.confirm.deleteBtn'),
      danger: true,
    });
    if (!ok) return;
    this.versionApi.tariffsTariffIdVersionsIdRateCapsRateCapIdDelete(tariff.id!, version.id!, cap.id!).subscribe({
      next: () => {
        this.expandedId.set(tariff.id ?? null);
        this.notify(this.t.instant('tariff.version.msg.capDeleted'));
        this.load();
      },
      error: (err) => this.notifyError(err, this.t.instant('tariff.version.msg.capDeleteFailed')),
    });
  }

  // ---- View helpers ----

  /** The version that actually applies now: the Active one, else the highest revision. */
  activeVersion(t: TariffDto): TariffVersionDto | null {
    const versions = t.versions ?? [];
    if (versions.length === 0) return null;
    const active = versions.find((v) => v.status === 'Active');
    if (active) return active;
    return [...versions].sort((a, b) => (b.revisionNumber ?? 0) - (a.revisionNumber ?? 0))[0] ?? null;
  }

  /** Headline price for a version, e.g. "€0.00 / hr" or "€2.00 / 2 hrs". */
  priceSummary(v: TariffVersionDto | null): string {
    const p = v?.pricingPolicy;
    if (!p) return '—';
    return `${this.money(p.baseAmount, p.baseCurrency)} / ${this.intervalLabel(p.interval, p.intervalCount)}`;
  }

  /** Money with a friendly symbol for common currencies. */
  money(amount: number | null | undefined, currency: string | null | undefined): string {
    const value = (amount ?? 0).toFixed(2);
    switch ((currency ?? '').toUpperCase()) {
      case 'EUR':
        return `€${value}`;
      case 'USD':
        return `$${value}`;
      case 'GBP':
        return `£${value}`;
      default:
        return `${value} ${currency ?? ''}`.trim();
    }
  }

  /** Billing interval label: interval + optional count, e.g. "hr", "2 hrs", "day". */
  intervalLabel(interval: string | null | undefined, count: number | null | undefined): string {
    const unit = (interval ?? '').toLowerCase();
    const short: Record<string, string> = { minute: 'min', hour: 'hr', day: 'day', week: 'wk', month: 'mo' };
    const label = short[unit] ?? (unit || 'interval');
    return count && count > 1 ? `${count} ${label}s` : label;
  }

  /** Human duration from minutes, e.g. 90 → "1h 30m", 45 → "45 min". */
  minutes(total: number | null | undefined): string {
    const value = total ?? 0;
    if (!value) return '0 min';
    const h = Math.floor(value / 60);
    const m = value % 60;
    if (h && m) return `${h}h ${m}m`;
    if (h) return `${h}h`;
    return `${m} min`;
  }

  /** Short local date (no time) for validity windows and audit. */
  shortDate(iso?: string | null): string {
    if (!iso) return '—';
    const d = new Date(iso);
    return isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
  }

  /** First segment of a GUID for compact display. */
  shortId(id?: string | null): string {
    return id ? id.split('-')[0] : '';
  }

  versionStatusClass(status: string | null | undefined): string {
    switch (status) {
      case 'Active':
        return 'bg-emerald-50 text-emerald-700 ring-emerald-600/20';
      case 'Superseded':
        return 'bg-slate-100 text-slate-500 ring-slate-500/20';
      default:
        return 'bg-blue-50 text-blue-700 ring-blue-600/20';
    }
  }

  /** The set scope fields on a rule, as label/value chips (skips empty ones). */
  /**
   * The rule's scope as display chips, ordered location -> product ladder -> parker -> grant so the
   * chips read left-to-right as "where, what, who". `tone` groups them by kind: the three levels of
   * the matching ladder (category, product, grant) are what decide which rule wins, so they are
   * worth telling apart at a glance from the plain location filters.
   */
  ruleFields(rule: TariffRuleDto): RuleScopeField[] {
    const fields: RuleScopeField[] = [];
    if (rule.spaceId) fields.push({ labelKey: 'space', value: rule.spaceName || this.shortId(rule.spaceId), tone: 'location' });
    if (rule.zoneId) fields.push({ labelKey: 'zone', value: rule.zoneName || this.shortId(rule.zoneId), tone: 'location' });
    if (rule.typeDefinitionId) fields.push({ labelKey: 'type', value: this.shortId(rule.typeDefinitionId), tone: 'ladder' });
    if (rule.parkingTypeId) fields.push({ labelKey: 'product', value: rule.parkingTypeName || this.shortId(rule.parkingTypeId), tone: 'ladder' });
    if (rule.organizationId) fields.push({ labelKey: 'org', value: rule.organizationName || this.shortId(rule.organizationId), tone: 'parker' });
    if (rule.classification && rule.classification !== 'Unspecified') fields.push({ labelKey: 'classification', value: rule.classification, tone: 'parker' });
    if (rule.parkingRightId) fields.push({ labelKey: 'right', value: this.shortId(rule.parkingRightId), tone: 'ladder' });
    return fields;
  }

  /** Tailwind classes per chip tone — see `ruleFields`. */
  chipClass(tone: RuleScopeField['tone']): string {
    switch (tone) {
      case 'ladder':
        return 'bg-blue-50 text-blue-700 ring-blue-600/20';
      case 'parker':
        return 'bg-violet-50 text-violet-700 ring-violet-600/20';
      default:
        return 'bg-slate-100 text-slate-600 ring-slate-500/20';
    }
  }

  statusClass(status: TariffStatus | null | undefined): string {
    switch (status) {
      case 'Active':
        return 'bg-emerald-50 text-emerald-700 ring-emerald-600/20';
      case 'Archived':
        return 'bg-amber-50 text-amber-700 ring-amber-600/20';
      default:
        return 'bg-slate-100 text-slate-600 ring-slate-500/20';
    }
  }

  // ---- Small utilities ----

  private confirm(data: ConfirmDialogData): Promise<boolean> {
    const withCancel: ConfirmDialogData = { cancelLabel: this.t.instant('tariff.confirm.cancel'), ...data };
    return firstValueFrom(
      this.dialog
        .open(ConfirmDialogComponent, { width: this.isHandset() ? '95vw' : '440px', maxWidth: this.isHandset() ? '95vw' : '440px', data: withCancel })
        .afterClosed(),
    ).then((r) => r === true);
  }

  private notify(message: string): void {
    this.snackBar.open(message, 'Close', { duration: 3000 });
  }

  private notifyError(err: unknown, fallback: string): void {
    console.error(err, fallback);
    const detail =
      (err as { error?: { detail?: string; title?: string } })?.error?.detail ??
      (err as { error?: { detail?: string; title?: string } })?.error?.title ??
      fallback;
    this.snackBar.open(detail, 'Close', { duration: 5000 });
  }
}
